import React from 'react'
import documentTrayIcon from '../../images/icons/document-tray-regular.svg'
import eyeIcon from '../../images/icons/eye-regular.svg'
import fileIcon from '../../images/icons/file-regular.svg'
import giftBoxIcon from '../../images/icons/gift-box-regular.svg'
import keyRoundIcon from '../../images/icons/key-round-regular.svg'
import layoutTemplateIcon from '../../images/icons/layout-template-regular.svg'
import mountainIcon from '../../images/icons/mountain-regular.svg'
import usersIcon from '../../images/icons/users-regular.svg'
import videoIcon from '../../images/icons/video-regular.svg'
import {
  MockBuyerInterest,
  MockClosing,
  MockDisclosures,
  MockDocTools,
  MockListingPackage,
  MockOffers,
  MockReports,
  MockTeamAccess,
  MockVideoTour,
} from './FeatureMocks'

const featureRows = [
  {
    id: 'offers',
    Mock: MockOffers,
    number: '01',
    kicker: 'Manage offers',
    title: 'Receive every offer in one secure place.',
    copy:
      'HomeLight Listing Management automatically organizes every offer into a dashboard that compares the terms side-by-side — price, finance type, contingencies, close date. The dashboard is sharable with your seller, so they decide on a screen instead of a stack of PDFs.',
    bullets: ['Side-by-side terms comparison', 'Finance type at a glance (cash vs. loan)', 'Sharable seller-facing dashboard', 'View offer and respond inline'],
    highlightIcon: documentTrayIcon,
    highlight: 'Send, receive, respond — in one place',
  },
  {
    id: 'disclosures',
    Mock: MockDisclosures,
    number: '02',
    kicker: 'Seller disclosures',
    title: 'Send disclosures faster, easier, safer — and it’s free.',
    copy:
      'Easy form filling for every disclosure doc you’re already using. HLM makes it seamless and straightforward for your clients to complete seller disclosures — preventing seller mistakes, limiting agent liability, and saving a ton of time.',
    bullets: [
      'Invite sellers with one click',
      'Form filling for every disclosure doc you use',
      'Limits agent liability with a clear paper trail',
      'Free to use',
    ],
    highlightIcon: giftBoxIcon,
    highlight: 'Disclosures & form filling are free',
  },
  {
    id: 'documents',
    Mock: MockDocTools,
    number: '03',
    kicker: 'Document Management',
    title: 'Edit, split & redact PDFs — without ever opening Adobe Acrobat.',
    copy:
      'Need to split a large document into smaller files, re-order pages, or redact private info? You have access to all of the powerful PDF manipulation tools you need, right inside the listing.',
    bullets: ['Split, annotate & rename pages', 'Replace, download or delete docs', 'Redact private info before sharing', 'Track who updated what, and when'],
    highlightIcon: fileIcon,
    highlight: 'No Acrobat license required',
  },
  {
    id: 'reports',
    Mock: MockReports,
    number: '04',
    kicker: 'Order reports',
    title: 'Order Prelim & NHD reports without picking up the phone.',
    copy:
      'Order a Preliminary Title Report and a Natural Hazards Disclosure right from the listing. The reports drop into the document package automatically — no phone tag, no chasing emails.',
    bullets: [
      'Order Prelim from inside the listing',
      'Order NHD report in two clicks',
      'Reports auto-attach to the package',
      'Status visible to the whole team',
    ],
    highlightIcon: mountainIcon,
    highlight: 'One-click vendor ordering',
  },
  {
    id: 'listing_information_package',
    Mock: MockListingPackage,
    number: '05',
    kicker: 'Listing Information Package',
    title: 'Build a branded property page that does the explaining for you.',
    copy:
      'Create digital Listing Information Packages that are easy to share and track. A Listing Information Package contains the supplemental documents that buyers won’t find on real estate portals like Zillow — price, beds & baths, lot size, virtual tour link, and disclosures.',
    bullets: [
      'Branded property page with photos',
      'Beds, baths, sqft, lot size, year built',
      'Virtual tour link & supplemental docs',
      'Buyer agents request access in one form',
    ],
    highlightIcon: layoutTemplateIcon,
    highlight: 'The supplemental info Zillow doesn’t have',
  },
  {
    id: 'video_tour',
    Mock: MockVideoTour,
    number: '06',
    kicker: 'Video Tour',
    title: 'Record a short, up-to 30-second clip for each room.',
    copy:
      'Walk the property and record a clip for each room or section of the home. Buyers re-watch the parts they care about, narrated in your voice. No editing software, no production crew — just your phone.',
    bullets: [
      '30-second clips per room or section',
      'Tips overlay for great captures',
      'Narrate the best features in your voice',
      'Tour bundles into the Listing Package',
    ],
    highlightIcon: videoIcon,
    highlight: 'No editing required',
  },
  {
    id: 'buyer_interest_reports',
    Mock: MockBuyerInterest,
    number: '07',
    kicker: 'Buyer Interest Reports',
    title: 'See who opened, read, and re-read every document.',
    copy:
      'Email wasn’t designed for large attachments — and it won’t let you track activity for each viewer. Buyer Interest Reports break down the open, read, and %-read of every document by every buyer party. Share the reports with your seller, too.',
    bullets: ['Open, read & %-read by viewer', 'Full timeline of viewership activity', 'Email, text & iOS app notifications', 'Sharable with the seller'],
    highlightIcon: eyeIcon,
    highlight: 'Spot interest before the offer arrives',
  },
  {
    id: 'listing_team_shared_access',
    Mock: MockTeamAccess,
    number: '08',
    kicker: 'Listing Team Shared Access',
    title: 'Collaborate with your whole listing team in one place.',
    copy:
      'HomeLight Listing Management is trusted by transaction coordinators, assistants, realtors, brokers & brokerages. Invite your whole team to manage, store, and monitor viewer activity on listing documents — in one secure location.',
    bullets: [
      'Invite TCs, assistants & brokers',
      'Per-person access (Owner / Edit / View)',
      'Brokerage-wide compliance access',
      'One secure location for the whole team',
    ],
    highlightIcon: usersIcon,
    highlight: 'Every role on the listing, in one place',
  },
  {
    id: 'closing_services',
    Mock: MockClosing,
    number: '09',
    kicker: 'Closing Services',
    title: 'Give clients a great closing with HomeLight.',
    copy:
      'A modern escrow experience designed for top agents, their teams, and their clients. A single, secure place where all closing documents are easily accessible, anytime — with industry-leading security and automated notifications keeping everyone on track.',
    bullets: [
      'Modern, fully-digital escrow',
      'All closing docs in one secure place',
      'Automated notifications for every party',
      'Designed for top agents and their teams',
    ],
    highlightIcon: keyRoundIcon,
    highlight: 'A great closing, every time',
  },
]

function FeatureHighlighter({icon, children}) {
  return (
    <div className="hlm-feature-highlighter">
      <span className="hlm-feature-highlighter-icon" style={{'--hlm-feature-highlighter-icon': `url(${icon})`}} aria-hidden="true" />
      <span>{children}</span>
    </div>
  )
}

function FeaturesV2() {
  return (
    <section id="features" className="hlm-features-section">
      <div className="hlm-features-intro">
        <div className="hlm-container hlm-center">
          <h2 className="hlm-h2">
            Listing management, <span style={{color: '#46b6ff'}}>simplified.</span>
          </h2>
          <p className="hlm-lede hlm-lede-center">Everything you need to create, manage, and share — without the busywork.</p>
        </div>
      </div>
      {featureRows.map((feature, index) => {
        const Mock = feature.Mock
        return (
          <article className={`hlm-feature-row${index % 2 === 1 ? ' is-reversed' : ''}`} key={feature.id}>
            <div className="hlm-container hlm-feature-grid">
              <div className="hlm-feature-copy">
                <div className="hlm-feature-pill">
                  <span className="hlm-feature-num">{feature.number}</span>
                  <span className="hlm-feature-kicker">{feature.kicker}</span>
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.copy}</p>
                <ul className="hlm-bullets">
                  {feature.bullets.map(bullet => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
                <FeatureHighlighter icon={feature.highlightIcon}>{feature.highlight}</FeatureHighlighter>
              </div>
              <div className="hlm-feature-mock">{Mock && <Mock />}</div>
            </div>
          </article>
        )
      })}
    </section>
  )
}

export default FeaturesV2
